import type { ImageProps } from '@mantine/core';
import { Image, useMantineColorScheme } from '@mantine/core';
import { integrationList } from '@repo/common/constants/integration/integrations';
import { EntityType } from '@repo/common/enums/entityType';
import { IconSkeleton } from '@repo/foundations';
import type { IconNames } from '@repo/foundations/components/Icon/types';
import { colors } from '@repo/theme/primitives';
import {
	IconBook,
	IconChartHistogram,
	IconFile,
	IconFolders,
	IconMessages,
} from '@tabler/icons-react';
import type { RefAttributes } from 'react';
import type { IIntegration } from '../api';
import { useIntegrationPrefetched } from '../api';

export const COLOR_MAP = {
	[EntityType.collection]: colors.teal[5],
	[EntityType.metric]: colors.primary[5],
	[EntityType.dictionary_term]: colors.primary[5],
	[EntityType.glossary]: colors.primary[5],
	[EntityType.document]: colors.indigo[5],
	[EntityType.question]: colors.yellow[5],
	[EntityType.all]: colors.blue[5],
};

export const iconSrcFromIntegration = (
	integration: IIntegration,
	isDarkMode = false
) => {
	if (integration.marketplace_integration_spec_version) {
		return integration.marketplace_integration_spec_version.icon_url;
	}

	if (integration.type === 'custom' && (integration as IIntegration).icon_url) {
		return (integration as IIntegration).icon_url;
	}

	const integrationSpec = integrationList.find(
		(i) => i.type?.toLowerCase() === integration.type.toLowerCase()
	);

	if (isDarkMode && integrationSpec?.srcDark) {
		return integrationSpec.srcDark;
	}
	return integrationSpec?.src;
};

export const vectorGraphicFromIntegrationType = (
	integrationType: string,
	isDarkMode = false
) => {
	const integrationSpec = integrationList.find(
		(i) => i.type?.toLowerCase() === integrationType?.toLowerCase()
	);

	if (isDarkMode && integrationSpec?.srcDark) {
		return integrationSpec.srcDark;
	}
	return integrationSpec?.src;
};

export const useIconSrcFromIntegrationId = (id: string) => {
	const { data: integration } = useIntegrationPrefetched({ id });
	const { colorScheme } = useMantineColorScheme();
	if (!integration) {
		return '';
	}
	return iconSrcFromIntegration(integration, colorScheme === 'dark');
};

export function getIconByEntityType(
	// By the order of precedence entity type > integration > integration type
	entityType?: EntityType,
	integration?: IIntegration,
	integrationType?: string,
	isDarkMode = false
) {
	switch (entityType) {
		case EntityType.collection:
			return {
				icon: IconFolders,
				backgroundColor: COLOR_MAP[entityType],
			};
		case EntityType.metric:
			return {
				icon: IconChartHistogram,
				backgroundColor: COLOR_MAP[entityType],
			};
		case EntityType.dictionary_term:
			return {
				icon: IconBook,
				backgroundColor: COLOR_MAP[entityType],
			};
		case EntityType.glossary:
			return {
				icon: IconBook,
				backgroundColor: COLOR_MAP[entityType],
			};
		case EntityType.document:
			return {
				icon: IconFile,
				backgroundColor: COLOR_MAP[entityType],
			};
		case EntityType.question:
			return {
				icon: IconMessages,
				backgroundColor: COLOR_MAP[entityType],
			};
		default:
			// eslint-disable-next-line no-case-declarations
			let src: string | undefined | null = null;
			if (integration) {
				src = iconSrcFromIntegration(integration, isDarkMode);
			} else if (integrationType) {
				src = vectorGraphicFromIntegrationType(integrationType, isDarkMode);
			}

			return {
				icon: src
					? (
							props: JSX.IntrinsicAttributes &
								ImageProps &
								RefAttributes<HTMLDivElement>
						) => <Image {...props} src={src} />
					: IconSkeleton,
				backgroundColor: COLOR_MAP[EntityType.all],
			};
	}
}

export function mapEntityTypeToIcon(
	entityType?: string
): { iconName: IconNames; iconColor: string } | null {
	switch (entityType) {
		case EntityType.collection:
			return { iconName: 'folder', iconColor: '#4a4a4a' };
		case EntityType.metric:
			return { iconName: 'chartHistogram', iconColor: '#4a4a4a' };
		case EntityType.dictionary_term:
		case EntityType.glossary:
			return { iconName: 'book', iconColor: '#4a4a4a' };
		case EntityType.document:
			return { iconName: 'file', iconColor: '#4a4a4a' };
		case EntityType.question:
			return { iconName: 'messages', iconColor: '#4a4a4a' };
		case EntityType.bi_dashboard:
			return { iconName: 'customDashboard', iconColor: '#4a4a4a' };
		default:
			return null;
	}
}
